import { useState } from 'react';
import { Bot, Send } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
}

interface AIAssistantProps {
  title?: string;
  initialMessage?: string;
  placeholder?: string;
  className?: string;
}

export function AIAssistant({
  title = 'AI Assistant',
  initialMessage = "Welcome! I'm here to help you. What would you like to create today?",
  placeholder = 'Ask AI for suggestions...',
  className,
}: AIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: initialMessage,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsGenerating(true);

    // Simulate AI response (replace with actual API call)
    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'This is a placeholder response. Connect to your AI service to get real suggestions.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsGenerating(false);
    }, 1500);
  };

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Header */}
      <div className="border-b border-ck-dark-border bg-ck-dark-card px-6 py-4">
        <h2 className="text-xl font-semibold text-ck-cyan">{title}</h2>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        {messages.map(message => (
          <div
            key={message.id}
            className={cn(
              'flex gap-3',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            {message.role === 'assistant' && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-ck-cyan to-ck-purple">
                <Bot className="h-5 w-5 text-white" />
              </div>
            )}
            <div
              className={cn(
                'max-w-[80%] rounded-lg px-4 py-3',
                message.role === 'assistant'
                  ? 'bg-ck-dark-card text-foreground'
                  : 'bg-ck-purple text-white'
              )}
            >
              <p className="text-sm leading-relaxed">{message.content}</p>
            </div>
          </div>
        ))}

        {isGenerating && (
          <div className="flex gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-ck-cyan to-ck-purple">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="rounded-lg bg-ck-dark-card px-4 py-3">
              <p className="text-sm text-muted-foreground">Generating suggestions...</p>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-ck-dark-border bg-ck-dark-card p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder={placeholder}
            className="flex-1 rounded-lg bg-background px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ck-cyan"
            disabled={isGenerating}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isGenerating}
            variant="gradient"
            size="icon"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}